import {
  InventoryTable,
  OrderApprovalHistory,
  OrderChangeShared,
  OrderInspectionTable,
  OrderNotes,
  OrderServiceSelect,
  OrderVehicleReminder,
  RelatedOrders,
  SharedChat,
  SharedTabs,
  VehicleDetailsCard,
  VehicleHistoryTable,
} from '@components';
import { useOverdueReminderCount } from '@hooks';
import { Order, Vehicle } from '@services';
import { useStore } from '@stores';
import { Badge } from 'antd';
import { observer } from 'mobx-react';
import { useView } from '@hooks';
import './OrderTabs.css';

interface IOrderTabsProps {
  order: Order | undefined;
  vehicle: Vehicle | undefined;
  activeTabKey: string;
  currentMileage: number | undefined;
  tabsRef: React.RefObject<HTMLDivElement | null>;
  onTabChange: (key: string) => void;
}

export const OrderTabs: React.FC<IOrderTabsProps> = observer(
  ({
    order,
    vehicle,
    activeTabKey,
    currentMileage,
    tabsRef,
    onTabChange,
  }) => {
    const { isMobile } = useView();
    const { authenticationStore } = useStore();
    const { overdueCount, loading } = useOverdueReminderCount(order?.vin);

    const currentUser = authenticationStore.currentUser;
    const userRole = currentUser?.role;

    const hideManagerOptions = userRole === 'Technician';

    const items = [
      {
        key: 'Job details',
        label: 'Job details',
        forceRender: true,
        children: (
          <OrderServiceSelect atLeastOneService />
        ),
      },
      {
        key: 'Inventory',
        label: 'Parts',
        children: <InventoryTable embedded />,
      },
      {
        key: 'RelatedJobs',
        label: 'Related work',
        children: (
          <RelatedOrders order={order} vehicle={vehicle} />
        ),
      },
      ...(!hideManagerOptions
        ? [
            {
              key: 'Reminders',
              label: (
                <Badge count={loading ? 0 : overdueCount} offset={[6, -2]} size="small">
                  Reminders
                </Badge>
              ),
              children: order && (
                <OrderVehicleReminder
                  vin={order?.vin || ''}
                  customerId={order.customerId}
                  currentMileage={currentMileage}
                  registrationNumber={order.registrationNumber}
                />
              ),
            },
          ]
        : []),
      {
        key: 'Vehicle',
        label: 'Vehicle',
        children: vehicle ? <VehicleDetailsCard vehicle={vehicle} /> : null,
      },
      {
        key: 'ServiceHistory',
        label: 'Service history',
        children: (
          <VehicleHistoryTable
            vin={order?.vin || ''}
            customerId={order?.customerId}
            customerEmail={order?.customer?.email}
            customerFullName={order?.customer?.fullname}
          />
        ),
      },
      {
        key: 'Inspections',
        label: 'Inspections',
        children: order && <OrderInspectionTable orderId={order.id} />,
      },

      {
        key: 'Estimates',
        label: 'Estimates',
        children: order ? (
          <OrderApprovalHistory
            orderId={order.id}
            customerEmail={order.customer?.email}
            customerId={order.customer?.id}
            customerUserId={order.customer?.userId}
            customerFullName={order.customer?.fullname}
            orderStatus={order.status}
          />
        ) : null,
      },
      {
        key: 'Chat',
        label: 'Chat',
        children: order?.customer?.userId ? (
          <SharedChat
            chatClassName="order-tabs-chat"
            hideChannelList
            channelID={order?.requestId ?? order?.id}
            filter={order?.customer.userId.toString()}
          />
        ) : (
          <>This customer is not a mobile app user</>
        ),
      },
      {
        key: 'History',
        label: 'History logs',
        children: order && <OrderChangeShared order={order} />,
      },
      {
        key: 'Notes',
        label: 'Notes',
        children: order && <OrderNotes order={order} />,
      },
      // {
      //   key: 'ServiceRepair',
      //   label: 'Service & Repair',
      //   children: order && (
      //     <VehicleHowToDoIt
      //       order={order}
      //       category={category || ''}
      //       vin={order?.vin ?? ''}
      //       setCategory={setCategory}
      //     />
      //   ),
      // },
      // {
      //   key: 'Bookmarks',
      //   label: 'Bookmarks',
      //   children: order ? <OrderBookmarkContainer orderId={order.id} /> : null,
      // },
    ];

    return (
      <div ref={tabsRef}>
        <div style={{ background: '#fff', borderRadius: 10, padding: '16px 16px 0', marginTop: 24 }}>
          <SharedTabs
            activeKey={activeTabKey}
            onChange={onTabChange}
            items={items}
            className={`order-tabs order-tabs-card${isMobile ? ' order-tabs-mobile' : ''}`}
          />
        </div>
      </div>
    );
  }
);
